
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { LogOut } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useGymActions } from "@/hooks/gym/useGymActions";
import { useProfile } from "@/hooks/useProfile";
import { GymMessageForm } from "./GymMessageForm";

interface CurrentGymProps {
  gymName: string;
  onLeaveGym: () => void;
  showLeaveButton?: boolean;
  gymId?: string;
}

export const CurrentGym = ({ gymName, onLeaveGym, showLeaveButton = true, gymId }: CurrentGymProps) => {
  const [code, setCode] = useState("");
  const { profile, updateProfile } = useProfile();
  const { isLoading, joinGym } = useGymActions(profile, updateProfile);
  const effectiveGymId = gymId ?? profile?.gym_id ?? null;

  const handleJoinGym = async () => {
    const success = await joinGym(code);
    if (success) {
      setCode("");
    }
  };

  return (
    <div className="space-y-4">
      <div className="gym-info-section">
        <Label>Tu gimnasio</Label>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center justify-between">
            <p className="text-foreground">{gymName}</p>
            {showLeaveButton && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <LogOut className="h-4 w-4 mr-2" />
                    Abandonar
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esta acción no se puede deshacer. Perderás acceso a las clases y servicios del gimnasio.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={onLeaveGym} className="bg-destructive hover:bg-destructive/90">
                      Abandonar gimnasio
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
      </div>

      {effectiveGymId && profile?.role !== "manager" && profile?.role !== "admin" && profile?.role !== "recepcionista" && (
        <GymMessageForm gymId={effectiveGymId} />
      )}

      <div className="join-gym-section space-y-2">
        <Label htmlFor="gymCode">Unirse a otro gimnasio</Label>
        <div className="flex gap-2">
          <Input
            id="gymCode"
            placeholder="Ingresa el código de 6 caracteres"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            maxLength={6}
            className="bg-background"
            disabled={isLoading}
          />
          <Button 
            onClick={handleJoinGym}
            disabled={isLoading || code.length !== 6}
          >
            {isLoading ? "Uniéndose..." : "Unirse"}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          El código debe tener exactamente 6 caracteres
        </p>
      </div>
    </div>
  );
};
